import type { Value } from 'platejs';
import type { MentionUser } from './mention';

export type ToolbarVariant = 'minimal' | 'slim' | 'full';

export interface PlateEditorProps {
    className?: string;
    placeholder?: string;
    initialValue?: Value;
    readOnly?: boolean;
    hideToolbar?: boolean;
    fullHeight?: boolean;
    onChange?: (value: Value) => void;
    toolbarVariant?: ToolbarVariant;
    toolbarHostId?: string;
    onEditorMount?: (editor: any) => void;
    /**
     * Backend tag used when uploading pasted / dropped images. Threaded to
     * `uploadSingleImage(dataUrl, uploadSource)`. Default 'one-pager' for
     * back-compat with the original consumer; set per-consumer when reusing
     * the editor elsewhere (e.g. 'engagement-letter', 'application-form').
     */
    uploadSource?: string;
    /** Enable AI command menu (Cmd+J). Backed by the askAica streaming agent. */
    enableAI?: boolean;
    /**
     * Subject company org id for the askAica RAG agent (the AI command menu).
     * For the One Pager this is the deal's `assesseeOrgId`. When omitted the
     * menu falls back to the investor org.
     */
    aiOrgId?: string;
    /**
     * Extra company chips appended to the AI menu's no-selection chip row,
     * scoped to this editor instance (e.g. the compose-email pitch-draft chip).
     */
    aiExtraCompanyChips?: string[];
    /**
     * If provided, registers the @platejs/mention plugin so users can type
     * `@` to autocomplete. Each row is rendered in the combobox; on select
     * a `mention` inline node is inserted with `{ value: name, key: userId }`.
     * Pass `state.investor.ownerDropdownOptions` (or an equivalent slice).
     * Omit / pass `[]` to disable @-mentions entirely.
     */
    mentionableUsers?: MentionUser[];
    /**
     * Drops the "A4 page" content styling (72×96 padding, 640px min-height,
     * page-card border + shadow) for narrow contexts like comment inputs
     * and the compose-email body field. Keep `false` (default) for true
     * document surfaces — one-pager, engagement letter.
     */
    compact?: boolean;
    /**
     * Cap the editable area at a fixed height with an internal scrollbar,
     * instead of letting it grow with content. Accepts any CSS length
     * (number → px, or a string like '12.5rem'). Used by compact inputs such
     * as the comments box so a long comment scrolls inside the box rather than
     * pushing the surrounding UI off-screen (RECUR-26761 AC1). Leave unset for
     * document surfaces (one-pager, engagement letter) that use `fullHeight`.
     */
    maxHeight?: number | string;
    /**
     * Strip foreign text styling (colour / font size / family / background) and
     * flatten layout tables on paste, keeping links + lists + basic marks. Used
     * by compact inputs like the comments box so pasted rich HTML (e.g. a Fathom
     * meeting-summary email) lands as clean text rather than washed-out,
     * oversized, table-wrapped content (RECUR-26761). Leave `false` (default) for
     * document editors (one-pager, engagement letter) that want full paste fidelity.
     */
    simplifyPaste?: boolean;
    /**
     * Hide specific inline-mark buttons from the toolbar. Currently
     * recognized: 'code' | 'subscript' | 'superscript'. The marks themselves
     * stay registered as plugins so pasted content with those marks still
     * round-trips — only the toolbar UI is suppressed.
     */
    hiddenMarks?: Array<'code' | 'subscript' | 'superscript'>;
    /**
     * Opt in to block drag-and-drop reordering (the gutter drag handle).
     * When true, registers NodeIdPlugin + DndPlugin (which wraps every block
     * in <BlockDraggable>) and mounts a per-instance <DndProvider>. Default
     * `false` — leaving it off keeps the editor DOM identical to the
     * pre-feature shape so two editors can co-mount without react-dnd's
     * "two HTML5 backends" crash, and so the BlockDraggable wrapper never
     * sits between PlateContent and the slate block during PDF export.
     */
    enableBlockDnd?: boolean;
    /**
     * Set to true when the editor is rendered inside a portal (e.g.
     * BottomDrawer). Disables blur-based cancellation for the mention
     * combobox, which otherwise fires spuriously in portaled contexts.
     */
    renderedInBottomSheet?: boolean;
    /**
     * Show the automatic, editor-only page-boundary indicator (RECUR-27304,
     * "see the pages"): a subtle seam band + "Page N" chip drawn at every
     * measured A3 page-height interval (see `usePageBoundaries` /
     * `PageBoundaryOverlay`). Purely a view-derived overlay — never a
     * document node, so it never affects save/load/PDF export. Default
     * `false`; threaded from the same two call sites (One Pager always on;
     * Reports on for `REPORTS`/`ONE_PAGER`) so every other surface stays off.
     */
    showPageGaps?: boolean;
}

export const EMPTY_EDITOR_VALUE: Value = [{ type: 'p', children: [{ text: '' }] }];

/**
 * React context propagating per-editor configuration (upload source,
 * potentially more in future) to deeply-nested children — toolbar buttons,
 * element renderers — without prop-drilling through Plate's plugin system.
 */
export interface PlateEditorConfigContextValue {
    uploadSource: string;
    mentionableUsers: MentionUser[];
    renderedInBottomSheet?: boolean;
}
